import type { Policy } from '../types/content';

export const policies: Policy[] = [
  {
    slug: 'terms-and-conditions',
    name: 'Terms and Conditions',
    metaTitle: 'Terms and Conditions | ES World English School',
    metaDescription: 'Read the ES World terms and conditions. Clear rules for booking, paying, attending and changing your course.',
    h1: 'Terms and conditions for your course',
    lead: 'These terms and conditions explain the agreement between you and ES World when you book a course. Please read them before you pay. If something is not clear, our team is happy to explain it.',
    sections: [
      { title: 'Your booking, made clear', paragraphs: [
        'Your booking is confirmed when you receive an offer letter and pay the deposit or full fee. The offer letter shows your course, dates, location and price.',
        'Please check every detail in your offer letter. Tell us about any mistakes within 000 days.',
      ] },
      { title: 'How payment works', paragraphs: [
        'You pay a deposit of 000 when you book. You pay the rest of the fee 000 days before your course starts. For bookings made later, you pay the full fee at once.',
        'Prices include lessons and course materials unless your offer letter says something different.',
      ] },
      { title: 'Your place in class', paragraphs: [
        'You take a level test before you start. We place you in a class at your level. Your teacher may move you to another class if this helps your progress.',
        'Please attend every lesson. Many visas need a set attendance level. We tell you if your attendance becomes too low.',
      ] },
      { title: 'Changes you can request', paragraphs: [
        'You can ask to change your dates, course or location. We try to help, but changes depend on available places. A fee of 000 may apply.',
        'Public holidays are not class days. We do not replace lessons on these days.',
      ] },
      { title: 'Respect for everyone', paragraphs: [
        'Everyone at ES World must treat other people with respect. We may end a course if a student breaks school rules in a serious way.',
      ] },
    ],
  },
  {
    slug: 'delivery-policy',
    name: 'Delivery Policy',
    metaTitle: 'Delivery Policy | How Your Course Is Delivered | ES World',
    metaDescription: 'Read how ES World delivers your course, documents and certificate. Clear steps from booking to your last class.',
    h1: 'Delivery policy: how we deliver your course',
    lead: 'This delivery policy explains how and when you receive your course, your documents and your certificate. Our courses are services, so there are no physical goods to ship unless we say so.',
    sections: [
      { title: 'Documents sent to you', paragraphs: [
        'After you book, we send your offer letter and invoice by email within 000 working days. We send visa letters by email after we receive your payment.',
        'If you need a printed letter, ask our team. Courier fees of 000 may apply.',
      ] },
      { title: 'Lessons at your campus', paragraphs: [
        'We deliver lessons at the campus shown in your offer letter. Your timetable is in the student portal before your first day.',
        'If we need to move a class to another room or time, we tell you as early as possible.',
      ] },
      { title: 'Online access you receive', paragraphs: [
        'You receive login details for the student portal by email. The portal includes your timetable, materials and progress reports.',
      ] },
      { title: 'Your certificate at the end', paragraphs: [
        'You receive a digital certificate on your last day if your attendance meets the course rules. Printed certificates are available at the campus.',
        'If you need a copy later, write to inquiries@esworld.com.',
      ] },
    ],
  },
  {
    slug: 'refund-policy',
    name: 'Refund Policy',
    metaTitle: 'Refund Policy | Cancellations and Refunds | ES World',
    metaDescription: 'Read the ES World refund policy. See what happens if you cancel your course, your visa is refused or you leave early.',
    h1: 'Refund policy for cancelled courses',
    lead: 'This refund policy explains when you can receive money back if you cancel or change your course. The rules depend on when you cancel and why. Please read this page before you pay.',
    sections: [
      { title: 'If you cancel before you start', paragraphs: [
        'If you cancel more than 000 days before your course starts, we refund your fees minus the registration fee.',
        'If you cancel less than 000 days before your course starts, we keep a cancellation fee of 000.',
      ] },
      { title: 'If your visa is refused', paragraphs: [
        'If your visa is refused, send us the official refusal letter. We refund your fees minus the registration fee and any visa fees we paid for you.',
      ] },
      { title: 'If you leave your course early', paragraphs: [
        'We do not usually refund fees after your course starts. In special cases, such as serious illness, we look at each request on its own.',
        'You may be able to move unused weeks to new dates within 000 months.',
      ] },
      { title: 'How you receive your refund', paragraphs: [
        'We send refunds to the person or company that paid. Refunds take up to 000 working days. Bank fees may apply.',
        'To ask for a refund, write to inquiries@esworld.com with your name and booking number.',
      ] },
    ],
  },
  {
    slug: 'privacy-policy',
    name: 'Privacy Policy',
    metaTitle: 'Privacy Policy | How We Protect Your Data | ES World',
    metaDescription: 'Read how ES World collects, uses and protects your personal data. Learn about your rights and how to contact us.',
    h1: 'Privacy policy: how we protect your data',
    lead: 'This privacy policy explains what personal data ES World collects, why we collect it and how we keep it safe. It also explains your rights and how to contact us about your data.',
    sections: [
      { title: 'Data we collect from you', paragraphs: [
        'We collect the details you give us when you send an enquiry or book a course. This includes your name, email address, phone number, nationality and course choices.',
        'For visa letters, we may also collect a copy of your passport.',
      ] },
      { title: 'How we use your data', paragraphs: [
        'We use your data to answer your questions, manage your booking, prepare visa documents and support you during your course.',
        'We only send marketing emails if you agree. You can stop these emails at any time.',
      ] },
      { title: 'Who can see your data', paragraphs: [
        'Only staff who need your data can see it. We share data with accommodation and transfer providers only when you book these services.',
        'We never sell your personal data.',
      ] },
      { title: 'Your rights and choices', paragraphs: [
        'You can ask to see, correct or delete your data. Write to inquiries@esworld.com and we will reply within 000 days.',
      ] },
      { title: 'How long we keep data', paragraphs: [
        'We keep booking records for 000 years for legal reasons. We delete enquiry data after 000 months if you do not book.',
      ] },
    ],
  },
];
